/**
 * Linked tiles: resolving a tile's `chartId` to the catalog chart it draws.
 *
 * A linked tile is Grafana's library panel - it holds a reference, and the
 * recipe comes from the catalog every time it is drawn, so editing the chart
 * there changes every dashboard that uses it. That makes the catalog a
 * dependency of the tile, and a dependency can be missing in more ways than a
 * recipe on the tile can. Each gets its own state here rather than a generic
 * error, because each wants a different sentence and a different way out:
 *
 * - **loading** - availability or the list is still on its way.
 * - **missing** - deleted, or no longer shared with you. The server answers
 *   both with 404 on purpose, so they are one state.
 * - **unavailable** - no database behind this deployment, or unreachable. The
 *   app without a database must behave as it did before the catalog existed,
 *   so this is a stated condition, not a failure.
 * - **error** - anything else, with a retry.
 *
 * Resolution reads the catalog's own list first (`scope=all`, one request for
 * every tile on the page), and only asks for a single chart by id when the list
 * does not have it - root opening a dashboard that links somebody's private
 * chart is the case that needs it.
 */

import { getChart } from '../../api/endpoints.ts';
import { ApiError } from '../../api/client.ts';
import type { CatalogChart } from '../../api/types.ts';
import { Atom, catalogAvailabilityKnown, catalogAvailable } from '../../state/store.ts';
import {
  catalogCharts,
  catalogError,
  catalogLoaded,
  catalogLoading,
  loadCatalog,
} from '../catalog/catalog-state.ts';
import { recipeFromChart, type DashboardTile } from './dashboard-state.ts';
import { userMessage } from '../../api/errors.ts';

export type LinkState =
  | { kind: 'ready'; chart: CatalogChart }
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string };

/**
 * Charts fetched one at a time because the list did not have them: the chart,
 * or null when the server said it does not exist for this viewer.
 */
export const fetchedCharts = new Atom<Map<string, CatalogChart | null>>(new Map());
/** Single-chart failures other than "not found", kept so a retry can clear them. */
const fetchErrors = new Map<string, string>();
const pending = new Set<string>();

/** Every atom resolution reads - what a component subscribes to. */
export const LINK_ATOMS = [
  catalogCharts,
  catalogLoaded,
  catalogLoading,
  catalogError,
  catalogAvailable,
  catalogAvailabilityKnown,
  fetchedCharts,
] as const;

/**
 * What a linked tile can draw right now. Kicks off whatever load is missing
 * and returns `loading` meanwhile; call again when `LINK_ATOMS` change.
 */
export function linkState(chartId: string): LinkState {
  if (!catalogAvailabilityKnown.value) return { kind: 'loading' };
  if (!catalogAvailable.value) return { kind: 'unavailable' };

  const listed = catalogCharts.value.find((c) => c.id === chartId);
  if (listed) return { kind: 'ready', chart: listed };

  if (!catalogLoaded.value) {
    if (catalogError.value) return { kind: 'error', message: catalogError.value };
    // Deferred: this runs during render, and starting a load writes atoms that
    // every other tile on the page is rendering from.
    if (!catalogLoading.value) queueMicrotask(() => void loadCatalog());
    return { kind: 'loading' };
  }

  const fetched = fetchedCharts.value.get(chartId);
  if (fetched) return { kind: 'ready', chart: fetched };
  if (fetched === null) return { kind: 'missing' };
  const failed = fetchErrors.get(chartId);
  if (failed) return { kind: 'error', message: failed };
  queueMicrotask(() => void fetchOne(chartId));
  return { kind: 'loading' };
}

async function fetchOne(chartId: string): Promise<void> {
  if (pending.has(chartId)) return;
  pending.add(chartId);
  try {
    const chart = await getChart(chartId);
    fetchedCharts.update((m) => new Map(m).set(chartId, chart));
  } catch (error) {
    if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
      fetchedCharts.update((m) => new Map(m).set(chartId, null));
    } else {
      fetchErrors.set(chartId, userMessage(error));
      fetchedCharts.notify();
    }
  } finally {
    pending.delete(chartId);
  }
}

/** Forget a failure so the next `linkState` asks again. */
export function retryLink(chartId: string): void {
  fetchErrors.delete(chartId);
  if (catalogError.value && !catalogLoaded.value) {
    void loadCatalog();
    return;
  }
  const next = new Map(fetchedCharts.value);
  next.delete(chartId);
  fetchedCharts.set(next);
}

/** The tile as it draws: the linked chart's current recipe over the tile's own. */
export function tileWithChart(tile: DashboardTile, chart: CatalogChart): DashboardTile {
  return { ...tile, ...recipeFromChart(chart) };
}

/**
 * The tile to draw, open or copy from: resolved through its chart when it has
 * one that resolves, otherwise as stored. The stored recipe of a linked tile is
 * the one it was linked with - right for Detach, and the best there is for
 * "Open in Explore" on a chart that has gone.
 */
export function effectiveTile(tile: DashboardTile): DashboardTile {
  if (!tile.chartId) return tile;
  const link = linkState(tile.chartId);
  return link.kind === 'ready' ? tileWithChart(tile, link.chart) : tile;
}

/** Test seam: drop everything this module has remembered. */
export function resetLinkedCharts(): void {
  fetchedCharts.set(new Map());
  fetchErrors.clear();
  pending.clear();
}
